import {createContext, useCallback} from 'react';

import {parsers} from './parser';

import {
  FallbackValues,
  FeatureFlagContext,
  FeatureFlags,
  FlagValueGetter,
} from './FeatureFlagProvider.types';

export const featureFlagContext = createContext<FeatureFlagContext>({
  getFlagValue: (_, fallbackValue) => fallbackValue as any,
});

/**
 * Provides context values for getting the values of feature flags, as defined
 * by the keys in the accompanying `parsers.ts` file.
 *
 * The flag will be matched against a key in the parsers, and the function for
 * that key (ie, the "parser"), will be called. A parser function may take an
 * optional fallback value, and must return a Promise that resolves to the
 * value of the feature flag. Otherwise, the parser may derive the feature flag
 * value however it wishes to (eg, fetching remotely, using an env var or cookie
 * value, etc).
 */
export const FeatureFlagProvider = ({children}: {children?: React.ReactNode}) => {
  const handleGetFlagValue = useCallback(
    async (flag: FeatureFlags, fallbackValue?: FallbackValues) => {
      const parserFn = parsers[flag];

      if (parserFn) {
        return await parserFn(fallbackValue as any);
      } else {
        throw new Error(`Unknown flag: ${flag}`);
      }
    },
    [],
  );

  return (
    <featureFlagContext.Provider value={{getFlagValue: handleGetFlagValue as FlagValueGetter}}>
      {children}
    </featureFlagContext.Provider>
  );
};
